import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { RefreshCw, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { auditsApi } from '@/api/client'
import type { AuditRequest, ReportDetail, SourceSpec } from '@/api/client'

interface Props {
  report: ReportDetail
  /** Defaults to "Re-run audit" — pass shorter copy when space is tight. */
  label?: string
}

/**
 * Re-runs an existing audit with the same URL, device, and (when present) source
 * attachment, then navigates to the live audit page. The audit page reads the
 * `compareWithId` location-state on `complete` and redirects to {@code /compare/old/new}.
 *
 * <p>For git sources, the PAT is intentionally NOT persisted — public repos
 * re-run cleanly, private repos require the user to start the run from HomePage
 * so they can re-enter the token. We surface that with a toast.</p>
 */
export default function ReRunAuditButton({ report, label = 'Re-run audit' }: Props) {
  const navigate = useNavigate()

  const mutation = useMutation({
    mutationFn: (req: AuditRequest) => auditsApi.start(req),
    onSuccess: data => {
      navigate(`/audit/${data.auditId}`, {
        state: { compareWithId: report.id },
      })
    },
    onError: () => {
      toast.error('Failed to start re-run. Is the BFF running?')
    },
  })

  const handleClick = () => {
    const source = rebuildSource(report)
    // Public git repos and local-folder sources re-run cleanly. Private git
    // repos will fail at clone time (no PAT) — that surfaces as a normal audit
    // error on the next page, telling the user to start from Home to re-enter
    // the token. No need to second-guess here.
    mutation.mutate({
      url: report.url,
      device: report.device,
      ...(source && { source }),
    })
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={mutation.isPending}
      className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border bg-white/5 border-white/10 hover:border-accent/40 hover:text-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ color: 'var(--text-secondary)' }}
    >
      {mutation.isPending ? (
        <><Loader2 size={12} className="animate-spin" /> Starting…</>
      ) : (
        <><RefreshCw size={12} /> {label}</>
      )}
    </button>
  )
}

/**
 * Reconstructs a {@link SourceSpec} from the persisted identifier.
 * Returns null for git sources because we can't supply a token here — caller
 * decides what to do (typically redirect to HomePage so the user re-enters it).
 */
function rebuildSource(report: ReportDetail): SourceSpec | null {
  if (!report.sourceKind || !report.sourceIdentifier) return null

  if (report.sourceKind === 'LOCAL') {
    return { kind: 'local', path: report.sourceIdentifier }
  }

  // For GIT we returned the scrubbed `url[#ref]` identifier. Public repos work
  // without a token; private repos will fail and the caller surfaces a toast.
  if (report.sourceKind === 'GIT') {
    const hashIdx = report.sourceIdentifier.lastIndexOf('#')
    const url = hashIdx >= 0
      ? report.sourceIdentifier.slice(0, hashIdx)
      : report.sourceIdentifier
    const ref = hashIdx >= 0 ? report.sourceIdentifier.slice(hashIdx + 1) : ''
    return {
      kind: 'git',
      url,
      ...(ref && { ref }),
    }
  }

  return null
}
